'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from '@eliadmin/core/src/components/image/image-wrapped';
import React from 'react';

import type { Service } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Heading } from '../shared/heading';
import { HtmlDescription } from '../shared/html-description';
import { LuMinus, LuPlus } from 'react-icons/lu';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

type ServicesProps = {
  subtitle: string;
  title: string;
  description: string;
  locale: string;
  services: Service[];
};

export function Services({ subtitle, title, description, services, locale }: ServicesProps) {
  const sectionId = locale === 'sk' ? 'kto-sme' : 'who-we-are';
  const alternateSectionId = locale === 'sk' ? 'who-we-are' : 'kto-sme';

  return (
    <section
      id={sectionId}
      className={cn(
        'relative scroll-mt-8',
        'custom-section',
        'w-full gap-6 pt-6 sm:gap-8 sm:pt-10 lg:gap-12 lg:pt-12 xl:gap-16',
      )}
    >
      <span id={alternateSectionId} className="absolute left-0 top-0 scroll-mt-8" />
      <div
        className={cn(
          'custom-container',
          'flex flex-col items-start justify-start gap-6 md:flex-row md:items-end md:justify-between',
        )}
      >
        <Heading
          subtitle={subtitle}
          title={title}
          className={{
            title: locale === 'sk' ? 'w-full max-w-[500px]' : 'w-full max-w-[660px]',
          }}
        />
        <HtmlDescription
          content={`<p>${description}</p>`}
          className="w-full text-black/70 md:max-w-[505px]"
          size="base"
        />
      </div>
      <div
        className={cn(
          'custom-container',
          'relative z-10 grid w-full grid-cols-1 gap-6 md:grid-cols-2 lg:gap-8',
        )}
      >
        {services.map(service => (
          <ServiceCard service={service} key={service.slug} />
        ))}
      </div>
    </section>
  );
}

function ServiceCard({ service }: { service: Service }) {
  const [isActive, setIsActive] = React.useState<string>('');

  const { t } = useTranslations();

  return (
    <div className="relative col-span-1 h-[405px] w-full overflow-hidden sm:h-[505px] lg:h-[520px]">
      <span
        onClick={() => setIsActive(isActive === service.slug ? '' : service.slug)}
        className="absolute right-4 top-4 z-10 flex h-[44px] w-auto cursor-pointer items-center justify-center gap-2 bg-white px-6 text-sm font-normal text-black sm:right-6 sm:top-6 sm:text-base md:h-14 lg:right-8 lg:top-8 lg:px-8"
      >
        {isActive === service.slug ? (
          <>
            <LuMinus size={20} />
            {t('text1942')}
          </>
        ) : (
          <>
            <LuPlus size={20} />
            {t('text1858')}
          </>
        )}
      </span>
      <ExpandedContent service={service} isActive={service.slug === isActive} />
      <Image
        src={'/images/sign.svg'}
        alt={service.name}
        width={0}
        height={0}
        className="absolute left-4 top-8 z-10 h-3.5 w-auto sm:left-6 sm:top-10 sm:h-4 lg:left-8 lg:top-12 lg:h-6"
      />
      <h2 className="absolute bottom-4 left-4 z-[3] max-w-[200px] font-heading text-4xl font-medium text-white sm:bottom-6 sm:left-6 sm:text-5xl lg:bottom-8 lg:left-8 lg:text-6xl">
        {service.name}
      </h2>
      <div className="absolute inset-0 z-[1] h-full w-full bg-gradient-to-b from-black/20 to-black/70" />
      <Image
        src={service.image?.url ?? '/images/placeholder.png'}
        alt={resolveImageAlt(service?.image, service?.name)}
        width={0}
        height={0}
        className="absolute inset-0 z-0 h-full w-full object-cover object-center"
        sizes="(min-width: 768px) 50vw, 100vw"
      />
    </div>
  );
}

function ExpandedContent({ service, isActive }: { service: Service; isActive: boolean }) {
  return (
    <div
      className={cn(
        'absolute inset-0 z-[4] flex h-full w-full flex-col items-start justify-end gap-4 bg-[#F6F6F6] p-4 sm:p-6 lg:p-8',
        'transition-all',
        isActive ? 'translate-y-0' : 'translate-y-full',
      )}
    >
      <h2 className="font-heading text-4xl font-medium text-black sm:text-5xl lg:text-6xl">
        {service.name}
      </h2>
      <HtmlDescription
        content={service.short_description ?? ''}
        className="w-full text-left"
        size="base"
      />
    </div>
  );
}
